import type { AxiosError } from 'axios'
import { useMemo } from 'react'
import type {
  IDigitalFileSimplified,
  IDigitalFileWContent,
  IDigitalFileWUrl,
  TDigitalFile,
} from '@/modules/DigitalFile/DigitalFile'
import { convertDigitalFileToBase64 } from '@/utils/misc'

import { useSWRAxios } from './useSWRAxios'

//  ///////////////////////
//  MARK: TYPES
type THashUrlResponse = { fileUrl?: string }

//  ///////////////////////
//  MARK: EXPORTS
/**
 * Resolves a `DigitalFileSimplified` (as served by the backend) into something
 * renderable.
 *
 * The backend never returns `fileUrl` in JSON anymore (closes the IDOR that
 * leaked presigned S3 URLs for every file to any authenticated user). So:
 *
 * - When the file has an `awsS3KeyString`, we request a fresh presigned URL
 *   from `GET /api/digitalFile/presignedUrl?hash=...` (auth-gated, short-lived).
 * - When it does not (legacy DB-stored blobs with `fileContent` bytes), we
 *   fall back to `GET /api/digitalFile/<id>` and convert the payload to a
 *   base64 data URL.
 */
export function useDigitalFile(file: IDigitalFileSimplified | IDigitalFileWUrl | undefined): {
  data: TDigitalFile | undefined
  error: AxiosError | undefined
} {
  // Client-synthesized files may already carry a usable URL (e.g. an
  // attachment token URL constructed locally). Short-circuit without hitting
  // the network in that case.
  const prebuiltUrl = file && 'fileUrl' in file ? file.fileUrl : undefined
  const useHash = !prebuiltUrl && Boolean(file?.presignedUrlEndpoint ?? file?.awsS3KeyString)

  const { data, error } = useSWRAxios<THashUrlResponse | IDigitalFileWContent>(
    () => {
      if (!file || prebuiltUrl) {
        return false
      }

      if (file.presignedUrlEndpoint) {
        return file.presignedUrlEndpoint
      }

      return useHash
        ? `/api/digitalFile/presignedUrl?hash=${encodeURIComponent(file.awsS3KeyString!)}`
        : `/api/digitalFile/${file.id.toString()}`
    },
    {},
    true,
  )

  return useMemo(() => {
    if (!file) {
      return { data: undefined, error }
    }

    if (prebuiltUrl) {
      return { data: file as IDigitalFileWUrl, error: undefined }
    }

    if (!data) {
      return { data: undefined, error }
    }

    if (useHash) {
      const { fileUrl } = data.data as THashUrlResponse

      if (!fileUrl) {
        return { data: undefined, error }
      }

      const resolved: IDigitalFileWUrl = { ...file, fileUrl }
      return { data: resolved, error }
    }

    return { data: convertDigitalFileToBase64(data.data as IDigitalFileWContent), error }
  }, [data, error, file, prebuiltUrl, useHash])
}
